import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import TiptapEditor from './TiptapEditor';
import {
    FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiChevronUp, FiChevronDown,
    FiEyeOff, FiEye, FiImage, FiList, FiCheckCircle, FiMonitor, FiMessageSquare,
    FiUsers, FiArrowRight, FiMessageCircle, FiLayers, FiTrendingUp, FiSettings, FiMenu,
    FiSmartphone, FiCode, FiServer, FiCloud, FiDatabase, FiTarget,
    FiZap, FiShield, FiClock, FiTool, FiType
} from 'react-icons/fi';

export default function AdminHomeManager() {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);
    const [trustLogos, setTrustLogos] = useState([]);

    const [formData, setFormData] = useState({
        name: '',
        type: 'welcome',
        title: '',
        subtitle: '',
        content: '',
        data: null,
        btn_text: '',
        btn_link: '',
        order: 0,
        is_published: true
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const sectionTypes = [
        { value: 'welcome', label: 'Bienvenue (Intro + Slider)' },
        { value: 'trust', label: 'Barre de Confiance (Logos)' },
        { value: 'counters', label: 'Compteurs Animés' },
        { value: 'services', label: 'Domaines d\'intervention' },
        { value: 'featured', label: 'Réalisations à la Une' },
        { value: 'testimonials', label: 'Témoignages Clients' },
        { value: 'recruitment', label: 'Recrutement (Tribe)' },
        { value: 'cta', label: 'Appel à l\'action final' },
        { value: 'faq', label: 'Questions Fréquentes' },
        { value: 'elo', label: 'Expertise ELO' },
        { value: 'process', label: 'Notre Processus' },
        { value: 'stack', label: 'Stack Technique' },
        { value: 'stack', label: 'Stack Technique' },
        { value: 'assistant', label: 'Assistant Flottant (Chat)' },
        { value: 'hero_overlay', label: 'Texte du Hero (Accueil)' }
    ];

    const fetchSections = async () => {
        setLoading(true);
        try {
            const records = await pb.collection('home_sections').getFullList({
                sort: 'order',
            });
            setSections(records);
        } catch (error) {
            console.error('Error fetching sections:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSections();
        fetchTrustLogos();
    }, []);

    const fetchTrustLogos = async () => {
        try {
            const records = await pb.collection('references').getFullList({
                filter: 'logo != "" && published = true && in_slider = true',
                sort: '-created',
            });
            setTrustLogos(records);
        } catch (error) {
            console.error('Error fetching trust logos:', error);
        }
    };

    const handleEdit = (section) => {
        setEditingId(section.id);
        setFormData({
            name: section.name || '',
            type: section.type || 'welcome',
            title: section.title || '',
            subtitle: section.subtitle || '',
            content: section.content || '',
            data: section.data || (['counters', 'faq', 'process', 'testimonials'].includes(section.type) ? [] : null),
            btn_text: section.btn_text || '',
            btn_link: section.btn_link || '',
            order: section.order || 0,
            is_published: section.is_published !== false
        });
        setSelectedFile(null);
        setImagePreview(section.image ? pb.files.getUrl(section, section.image) : null);
    };

    const handleCancel = () => {
        setEditingId(null);
        setFormData({
            name: '', type: 'welcome', title: '', subtitle: '', content: '',
            data: null, btn_text: '', btn_link: '', order: 0, is_published: true
        });
        setSelectedFile(null);
        setImagePreview(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('type', formData.type);
            data.append('title', formData.title);
            data.append('subtitle', formData.subtitle);
            data.append('content', formData.content);
            data.append('btn_text', formData.btn_text);
            data.append('btn_link', formData.btn_link);
            data.append('order', formData.order);
            data.append('is_published', formData.is_published);

            if (formData.data) {
                data.append('data', JSON.stringify(formData.data));
            }

            if (selectedFile) {
                data.append('image', selectedFile);
            }

            if (editingId && editingId !== 'new') {
                await pb.collection('home_sections').update(editingId, data);
            } else {
                await pb.collection('home_sections').create(data);
            }
            fetchSections();
            handleCancel();
        } catch (error) {
            console.error('Error saving section:', error);
            alert('Erreur lors de la sauvegarde');
        } finally {
            setIsSaving(false);
        }
    };

    const handleMove = async (index, direction) => {
        const newSections = [...sections];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newSections.length) return;

        const [moved] = newSections.splice(index, 1);
        newSections.splice(targetIndex, 0, moved);
        setSections(newSections);

        try {
            await Promise.all(newSections.map((sec, i) =>
                pb.collection('home_sections').update(sec.id, { order: i })
            ));
        } catch (error) {
            console.error('Error updating order:', error);
            fetchSections();
        }
    };

    const togglePublish = async (section) => {
        try {
            await pb.collection('home_sections').update(section.id, {
                is_published: !section.is_published
            });
            fetchSections();
        } catch (error) {
            console.error('Error toggling publish:', error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Supprimer cette section ?')) {
            try {
                await pb.collection('home_sections').delete(id);
                fetchSections();
            } catch (error) {
                console.error('Error deleting section:', error);
            }
        }
    };

    const handleDragStart = (e, index) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        // HTML5 Drag and Drop ghost image
        e.target.style.opacity = '0.5';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedItemIndex === index) return;

        // Dynamic reordering preview
        const newSections = [...sections];
        const draggedItem = newSections[draggedItemIndex];
        newSections.splice(draggedItemIndex, 1);
        newSections.splice(index, 0, draggedItem);

        setDraggedItemIndex(index);
        setSections(newSections);
    };

    const handleDragEnd = async (e) => {
        e.target.style.opacity = '1';
        setDraggedItemIndex(null);

        // Save new order to database
        try {
            await Promise.all(sections.map((sec, i) =>
                pb.collection('home_sections').update(sec.id, { order: i })
            ));
        } catch (error) {
            console.error('Error saving order after drag:', error);
            fetchSections();
        }
    };

    // Helper to determine if a field is relevant for the selected section type
    const hasField = (fieldName) => {
        const type = formData.type;
        switch (fieldName) {
            case 'title': return !['trust'].includes(type);
            case 'subtitle': return ['welcome', 'services', 'featured', 'cta', 'faq', 'process', 'stack', 'assistant', 'recruitment', 'elo', 'hero_overlay'].includes(type);
            case 'content': return ['welcome', 'recruitment', 'elo', 'assistant'].includes(type);
            case 'buttons': return ['welcome', 'featured', 'recruitment', 'cta', 'elo'].includes(type);
            case 'image': return ['recruitment'].includes(type);
            case 'data': return ['counters', 'faq', 'process', 'testimonials', 'assistant', 'welcome', 'services', 'recruitment', 'elo'].includes(type);
            case 'label': return ['trust'].includes(type); // Special case for trust label
            default: return true;
        }
    };

    // Simple Data List Editor (for FAQs, Counters, etc.)
    const renderDataEditor = () => {
        const type = formData.type;
        const items = Array.isArray(formData.data) ? formData.data : [];

        const addItem = () => {
            let newItem = {};
            if (type === 'counters') newItem = { label: '', end: 0, suffix: '' };
            if (type === 'faq') newItem = { question: '', answer: '', color: '#3b82f6', icon: 'FiMessageSquare' };
            if (type === 'process') newItem = { num: items.length + 1, title: '', desc: '' };
            if (type === 'testimonials') newItem = { quote: '', author: '', company: '' };
            if (type === 'assistant') newItem = { label: '', anchor: '' };
            if (type === 'welcome' || type === 'recruitment' || type === 'elo') newItem = { text: '' };
            if (type === 'services') newItem = { title: '', desc: '', icon: 'FiMonitor' };
            setFormData({ ...formData, data: [...items, newItem] });
        };

        const updateItem = (idx, field, val) => {
            const newItems = [...items];
            newItems[idx][field] = val;
            setFormData({ ...formData, data: newItems });
        };

        const removeItem = (idx) => {
            const newItems = items.filter((_, i) => i !== idx);
            setFormData({ ...formData, data: newItems });
        };

        return (
            <div className="data-list-editor">
                <div className="data-list-header">
                    <label>Éléments de la liste ({items.length})</label>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={addItem}><FiPlus /> Ajouter</button>
                </div>
                <div className="data-items-grid">
                    {items.map((item, idx) => (
                        <div key={idx} className="data-item-card">
                            <button type="button" className="btn-remove-item" onClick={() => removeItem(idx)}><FiX /></button>
                            {type === 'counters' && (
                                <div className="item-row">
                                    <input placeholder="Label" value={item.label} onChange={e => updateItem(idx, 'label', e.target.value)} />
                                    <input type="number" placeholder="Valeur" value={item.end} onChange={e => updateItem(idx, 'end', parseInt(e.target.value))} style={{ width: '80px' }} />
                                    <input placeholder="Suffixe" value={item.suffix} onChange={e => updateItem(idx, 'suffix', e.target.value)} style={{ width: '60px' }} />
                                </div>
                            )}
                            {type === 'faq' && (
                                <div className="faq-item-editor">
                                    <div className="faq-selectors">
                                        <div className="icon-selector-grid mini">
                                            {[
                                                { id: 'FiMessageSquare', label: 'Bulbe', icon: <FiMessageSquare /> },
                                                { id: 'FiMonitor', label: 'Écran', icon: <FiMonitor /> },
                                                { id: 'FiZap', label: 'Éclair', icon: <FiZap /> },
                                                { id: 'FiShield', label: 'Sécurité', icon: <FiShield /> },
                                                { id: 'FiClock', label: 'Temps', icon: <FiClock /> },
                                                { id: 'FiUsers', label: 'Équipe', icon: <FiUsers /> },
                                                { id: 'FiTarget', label: 'Cible', icon: <FiTarget /> },
                                                { id: 'FiTrendingUp', label: 'Croissance', icon: <FiTrendingUp /> },
                                                { id: 'FiCode', label: 'Code', icon: <FiCode /> },
                                                { id: 'FiLayers', label: 'Couches', icon: <FiLayers /> },
                                                { id: 'FiSettings', label: 'Paramètres', icon: <FiSettings /> },
                                                { id: 'FiCheckCircle', label: 'Validé', icon: <FiCheckCircle /> }
                                            ].map(opt => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    className={`icon-opt ${item.icon === opt.id ? 'active' : ''}`}
                                                    onClick={() => updateItem(idx, 'icon', opt.id)}
                                                    title={opt.label}
                                                >
                                                    {opt.icon}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="color-selector-grid">
                                            {[
                                                '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#ef4444', '#6366f1'
                                            ].map(color => (
                                                <button
                                                    key={color}
                                                    type="button"
                                                    className={`color-opt ${item.color === color ? 'active' : ''}`}
                                                    onClick={() => updateItem(idx, 'color', color)}
                                                    style={{ backgroundColor: color }}
                                                    title={color}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <input placeholder="Question" value={item.question} onChange={e => updateItem(idx, 'question', e.target.value)} />
                                    <textarea placeholder="Réponse" value={item.answer} onChange={e => updateItem(idx, 'answer', e.target.value)} rows={2} style={{ marginTop: '0.5rem' }} />
                                </div>
                            )}
                            {type === 'process' && (
                                <>
                                    <div className="item-row">
                                        <input type="number" value={item.num} onChange={e => updateItem(idx, 'num', parseInt(e.target.value))} style={{ width: '60px' }} />
                                        <input placeholder="Titre de l'étape" value={item.title} onChange={e => updateItem(idx, 'title', e.target.value)} />
                                    </div>
                                    <textarea placeholder="Description" value={item.desc} onChange={e => updateItem(idx, 'desc', e.target.value)} rows={2} />
                                </>
                            )}
                            {type === 'testimonials' && (
                                <>
                                    <textarea placeholder="Citation" value={item.quote} onChange={e => updateItem(idx, 'quote', e.target.value)} rows={2} />
                                    <div className="item-row">
                                        <input placeholder="Auteur" value={item.author} onChange={e => updateItem(idx, 'author', e.target.value)} />
                                        <input placeholder="Entreprise" value={item.company} onChange={e => updateItem(idx, 'company', e.target.value)} />
                                    </div>
                                </>
                            )}
                            {type === 'assistant' && (
                                <div className="item-row">
                                    <input placeholder="Label du bouton" value={item.label} onChange={e => updateItem(idx, 'label', e.target.value)} />
                                    <input placeholder="ID d'ancrage (ex: elo-target)" value={item.anchor} onChange={e => updateItem(idx, 'anchor', e.target.value)} />
                                </div>
                            )}
                            {(type === 'welcome' || type === 'recruitment' || type === 'elo') && (
                                <div className="item-row">
                                    <FiCheckCircle style={{ color: '#10b981', marginTop: '12px' }} />
                                    <input placeholder="Ex: Dématérialisation de factures" value={item.text} onChange={e => updateItem(idx, 'text', e.target.value)} />
                                </div>
                            )}
                            {type === 'services' && (
                                <div className="service-item-editor">
                                    <div className="icon-selector-grid">
                                        {[
                                            { id: 'FiMonitor', label: 'Écran', icon: <FiMonitor /> },
                                            { id: 'FiLayers', label: 'Couches', icon: <FiLayers /> },
                                            { id: 'FiSmartphone', label: 'Mobile', icon: <FiSmartphone /> },
                                            { id: 'FiCode', label: 'Code', icon: <FiCode /> },
                                            { id: 'FiServer', label: 'Serveur', icon: <FiServer /> },
                                            { id: 'FiDatabase', label: 'Data', icon: <FiDatabase /> },
                                            { id: 'FiCloud', label: 'Cloud', icon: <FiCloud /> },
                                            { id: 'FiTrendingUp', label: 'Croissance', icon: <FiTrendingUp /> },
                                            { id: 'FiSettings', label: 'Écrous', icon: <FiSettings /> },
                                            { id: 'FiUsers', label: 'Équipe', icon: <FiUsers /> },
                                            { id: 'FiTarget', label: 'Cible', icon: <FiTarget /> },
                                            { id: 'FiMessageSquare', label: 'Chat', icon: <FiMessageSquare /> }
                                        ].map(opt => (
                                            <button
                                                key={opt.id}
                                                type="button"
                                                className={`icon-opt ${item.icon === opt.id ? 'active' : ''}`}
                                                onClick={() => updateItem(idx, 'icon', opt.id)}
                                                title={opt.label}
                                            >
                                                {opt.icon}
                                                <span>{opt.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="item-row mt-2">
                                        <input placeholder="Titre du service" value={item.title} onChange={e => updateItem(idx, 'title', e.target.value)} />
                                    </div>
                                    <textarea placeholder="Description courte (ex: Portails clients, applications métier...)" value={item.desc} onChange={e => updateItem(idx, 'desc', e.target.value)} rows={2} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (loading && sections.length === 0) return <div className="admin-loading">Chargement...</div>;

    return (
        <div className="admin-content-inner">
            <div className="admin-header-actions">
                <div>
                    <h2 className="admin-page-title">Gestion de l'Accueil</h2>
                    <p className="admin-page-subtitle">Pilotez le contenu, l'ordre et la visibilité des sections.</p>
                </div>
                <button className="btn btn-primary" onClick={() => {
                    setEditingId('new');
                    setFormData(prev => ({ ...prev, order: sections.length, type: 'welcome' }));
                }}>
                    <FiPlus /> Ajouter une section
                </button>
            </div>

            <div className="sections-list">
                {sections.map((sec, index) => {
                    const typeInfo = sectionTypes.find(t => t.value === sec.type);
                    return (
                        <div
                            key={sec.id}
                            className={`section-admin-item ${!sec.is_published ? 'is-draft' : ''} slide-up ${draggedItemIndex === index ? 'is-dragging' : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                        >
                            <div className="section-drag-handle">
                                <FiMenu />
                            </div>
                            <div className="section-drag-controls">
                                <button disabled={index === 0} onClick={(e) => { e.stopPropagation(); handleMove(index, 'up'); }} title="Monter"><FiChevronUp /></button>
                                <span className="order-num">{index + 1}</span>
                                <button disabled={index === sections.length - 1} onClick={(e) => { e.stopPropagation(); handleMove(index, 'down'); }} title="Descendre"><FiChevronDown /></button>
                            </div>

                            <div className="section-icon-preview">
                                {sec.type === 'welcome' && <FiMonitor />}
                                {sec.type === 'trust' && <FiCheckCircle />}
                                {sec.type === 'counters' && <FiPlus />}
                                {sec.type === 'services' && <FiList />}
                                {sec.type === 'featured' && <FiImage />}
                                {sec.type === 'testimonials' && <FiMessageSquare />}
                                {sec.type === 'recruitment' && <FiUsers />}
                                {sec.type === 'cta' && <FiArrowRight />}
                                {sec.type === 'faq' && <FiMessageCircle />}
                                {sec.type === 'elo' && <FiLayers />}
                                {sec.type === 'process' && <FiTrendingUp />}
                                {sec.type === 'stack' && <FiSettings />}
                                {sec.type === 'assistant' && <FiMessageCircle />}
                                {sec.type === 'hero_overlay' && <FiType />}
                            </div>

                            <div className="section-info">
                                <div className="section-meta">
                                    <span className="section-badge">{typeInfo?.label || sec.type}</span>
                                    {!sec.is_published && <span className="draft-badge"><FiEyeOff /> Masqué</span>}
                                </div>
                                <h4 className="section-name">{sec.name || 'Sans nom'}</h4>
                                <p className="section-excerpt">{sec.title || 'Pas de titre affiché'}</p>
                            </div>
                            <div className="section-item-actions">
                                <button
                                    className={`btn-icon-round ${!sec.is_published ? 'is-draft-btn' : 'is-published-btn'}`}
                                    onClick={() => togglePublish(sec)}
                                    title={sec.is_published ? "Dépublier" : "Publier"}
                                >
                                    {sec.is_published ? <FiEye /> : <FiEyeOff />}
                                </button>
                                <button className="btn-icon-round" onClick={() => handleEdit(sec)} title="Modifier"><FiEdit2 /></button>
                                <button className="btn-icon-round delete" onClick={() => handleDelete(sec.id)} title="Supprimer"><FiTrash2 /></button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* MODAL CENTRÉE */}
            {editingId && (
                <div className="admin-modal-overlay fade-in">
                    <div className="admin-modal-container zoom-in">
                        <form onSubmit={handleSave}>
                            <div className="modal-header">
                                <div>
                                    <h3>{editingId === 'new' ? 'Nouvelle Section' : 'Édition de Section'}</h3>
                                    <p className="modal-intro">{formData.name || 'Configuration visuelle'}</p>
                                </div>
                                <button type="button" className="close-modal" onClick={handleCancel}><FiX /></button>
                            </div>

                            <div className="modal-body custom-scrollbar">
                                <div className="admin-form-grid">
                                    <div className="form-group">
                                        <label>Nom interne</label>
                                        <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="ex: Intro V2" required />
                                    </div>

                                    <div className="form-group">
                                        <label>Type de Section</label>
                                        <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value, data: ['counters', 'faq', 'process', 'testimonials'].includes(e.target.value) ? [] : null })}>
                                            {sectionTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                                        </select>
                                    </div>

                                    {hasField('label') && (
                                        <div className="form-group full-width">
                                            <label>Texte du label (ex: "Ils nous font confiance")</label>
                                            <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
                                        </div>
                                    )}

                                    {hasField('title') && (
                                        <div className="form-group full-width">
                                            <label>Titre Principal (H2)</label>
                                            <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
                                        </div>
                                    )}

                                    {hasField('subtitle') && (
                                        <div className="form-group full-width">
                                            <label>Sous-titre / Intro courte</label>
                                            <textarea value={formData.subtitle} onChange={e => setFormData({ ...formData, subtitle: e.target.value })} rows={2} />
                                        </div>
                                    )}

                                    {hasField('content') && (
                                        <div className="form-group full-width">
                                            <label>Texte Enrichi (Contenu)</label>
                                            <div className="tiptap-modal-wrapper">
                                                <TiptapEditor content={formData.content} onChange={(html) => setFormData({ ...formData, content: html })} />
                                            </div>
                                        </div>
                                    )}

                                    {hasField('data') && (
                                        <div className="form-group full-width">
                                            {renderDataEditor()}
                                        </div>
                                    )}

                                    {hasField('buttons') && (
                                        <>
                                            <div className="form-group">
                                                <label>Texte du Bouton</label>
                                                <input type="text" value={formData.btn_text} onChange={e => setFormData({ ...formData, btn_text: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Lien (URL)</label>
                                                <input type="text" value={formData.btn_link} onChange={e => setFormData({ ...formData, btn_link: e.target.value })} />
                                            </div>
                                        </>
                                    )}

                                    {hasField('image') && (
                                        <div className="form-group">
                                            <label>Image d'illustration</label>
                                            <div className="image-upload-zone">
                                                {imagePreview ? (
                                                    <div className="preview-container">
                                                        <img src={imagePreview} alt="Preview" className="form-image-preview" />
                                                        <button type="button" className="btn-remove" onClick={() => { setSelectedFile(null); setImagePreview(null); }}><FiX /></button>
                                                    </div>
                                                ) : (
                                                    <label className="upload-placeholder">
                                                        <FiImage size={24} />
                                                        <span>Ajouter une image</span>
                                                        <input type="file" onChange={e => { const f = e.target.files[0]; if (f) { setSelectedFile(f); setImagePreview(URL.createObjectURL(f)); } }} hidden />
                                                    </label>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {formData.type === 'trust' && (
                                        <div className="form-group full-width">
                                            <label>Prévisualisation des logos ({trustLogos.length})</label>
                                            <div className="logos-preview-admin">
                                                {trustLogos.map(logo => (
                                                    <div key={logo.id} className="logo-preview-item" title={logo.client}>
                                                        <img src={pb.files.getUrl(logo, logo.logo, { thumb: '100x100' })} alt={logo.client} />
                                                        <span>{logo.client}</span>
                                                    </div>
                                                ))}
                                                {trustLogos.length === 0 && <p className="text-muted">Aucun logo configuré dans les Références.</p>}
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label className="chk-label">
                                            <input type="checkbox" checked={formData.is_published} onChange={e => setFormData({ ...formData, is_published: e.target.checked })} />
                                            <span>Section active et visible sur le site</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-ghost" onClick={handleCancel}>Annuler</button>
                                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                                    <FiSave /> {isSaving ? 'Enregistrement...' : (editingId === 'new' ? 'Créer la section' : 'Sauvegarder les modifications')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                /* Specific styles for Home Manager components that aren't global yet */
                .sections-list { display: flex; flex-direction: column; gap: 1rem; margin-top: 2rem; }

                /* MODAL STYLES (MATCHING CONTACT MANAGER) */
                .admin-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); z-index: 9999; display: flex; align-items: flex-start; justify-content: center; padding: 2rem; overflow-y: auto; }
                .admin-modal-container { background: var(--card-bg, #fff); width: 100%; max-width: 800px; border-radius: 24px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); border: 1px solid var(--border-subtle); overflow: hidden; }
                
                .modal-header { padding: 1.5rem 2rem; border-bottom: 1px solid var(--border-subtle); display: flex; justify-content: space-between; align-items: flex-start; }
                .modal-header h3 { margin: 0; font-size: 1.5rem; color: var(--text-main); }
                .modal-intro { margin: 4px 0 0; font-size: 0.9rem; color: var(--text-muted); }
                .close-modal { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: var(--text-muted); padding: 4px; border-radius: 50%; display: flex; transition: all 0.2s; }
                .close-modal:hover { background: rgba(0,0,0,0.05); color: #ef4444; }
                
                .modal-body { padding: 2.5rem; max-height: 70vh; overflow-y: auto; }
                .modal-footer { padding: 1.25rem 2rem; border-top: 1px solid var(--border-subtle); display: flex; justify-content: flex-end; gap: 1rem; background: var(--bg-subtle, #f8fafc); }

                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes zoomIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
                
                .fade-in { animation: fadeIn 0.3s ease-out; }
                .zoom-in { animation: zoomIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); }

                body.theme-dark .admin-modal-container { background: #1e293b; border-color: #334155; }
                body.theme-dark .modal-header, body.theme-dark .modal-footer { border-color: #334155; }
                body.theme-dark .modal-footer { background: #0f172a; }

                /* FORM STYLES (MATCHING CONTACT MANAGER) */
                .admin-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
                .form-group { margin-bottom: 0; }
                .form-group.full-width { grid-column: span 2; }
                .form-group label { display: block; font-weight: 600; margin-bottom: 0.5rem; color: #475569; font-size: 0.9rem; }
                .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 0.75rem; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 0.95rem; }
                
                .data-list-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
                .data-item-card { background: var(--bg-subtle, #f8fafc); border: 1px solid var(--border-subtle); border-radius: 12px; padding: 1rem; position: relative; margin-bottom: 1rem; }
                .btn-remove-item { position: absolute; top: 0.5rem; right: 0.5rem; border: none; background: none; color: #ef4444; cursor: pointer; padding: 4px; border-radius: 50%; display: flex; }
                .btn-remove-item:hover { background: #fee2e2; }
                .item-row { display: flex; gap: 0.5rem; margin-bottom: 0.5rem; }
                
                .chk-label { display: flex; align-items: center; gap: 0.75rem; cursor: pointer; font-weight: 600 !important; }
                .chk-label input { width: auto !important; }

                /* Logos Preview Admin */
                .logos-preview-admin { 
                    display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 1rem; 
                    background: var(--bg-subtle, #f8fafc); padding: 1.5rem; border-radius: 16px; border: 1px dashed var(--border-subtle);
                    max-height: 250px; overflow-y: auto;
                }
                .logo-preview-item { 
                    display: flex; flex-direction: column; align-items: center; gap: 0.5rem; text-align: center;
                    background: white; padding: 0.75rem; border-radius: 12px; border: 1px solid var(--border-subtle);
                }
                .logo-preview-item img { max-width: 60px; max-height: 40px; object-fit: contain; }
                .logo-preview-item span { font-size: 0.7rem; color: #64748b; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 100%; }

                /* Icon Selector for Services */
                .icon-selector-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
                    gap: 10px;
                    margin: 10px 0;
                    background: #f8fafc;
                    padding: 12px;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                }
                .icon-opt {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 12px 6px;
                    cursor: pointer;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    color: #64748b;
                }
                .icon-opt svg { font-size: 1.5rem; }
                .icon-opt span { font-size: 0.7rem; font-weight: 700; color: inherit; }
                .icon-opt:hover { 
                    border-color: #3b82f6; 
                    color: #3b82f6; 
                    background: #eff6ff;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.1);
                }
                .icon-opt.active { 
                    background: #3b82f6; 
                    color: white; 
                    border-color: #3b82f6;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }

                /* FAQ Color Selector */
                .faq-selectors { display: flex; flex-direction: column; gap: 10px; margin-bottom: 12px; }
                .color-selector-grid { 
                    display: grid; 
                    grid-template-columns: repeat(auto-fill, minmax(30px, 1fr)); 
                    gap: 8px;
                    background: #f1f5f9;
                    padding: 8px;
                    border-radius: 12px;
                }
                .color-opt { 
                    width: 24px; 
                    height: 24px; 
                    border-radius: 50%; 
                    border: 2px solid white; 
                    cursor: pointer; 
                    transition: all 0.2s;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                .color-opt:hover { transform: scale(1.2); }
                .color-opt.active { transform: scale(1.3); border-color: #3b82f6; box-shadow: 0 0 10px rgba(59, 130, 246, 0.4); }
                
                body.theme-dark .color-selector-grid { background: #0f172a; }
                body.theme-dark .color-opt { border-color: #1e293b; }
                body.theme-dark .color-opt.active { border-color: white; }
                body.theme-dark .icon-selector-grid { background: #0f172a; border-color: #334155; }
                body.theme-dark .icon-opt { background: #1e293b; border-color: #334155; color: #94a3b8; }
                body.theme-dark .icon-opt:hover { background: #1e3a8a; color: #60a5fa; border-color: #2563eb; }

                /* Dark Mode Overrides for Forms */
                body.theme-dark .form-group label { color: #94a3b8; }
                body.theme-dark .form-group input, body.theme-dark .form-group select, body.theme-dark .form-group textarea { background: #0f172a; border-color: #334155; color: white; }
                body.theme-dark .data-item-card { background: #0f172a; border-color: #334155; }
                
                .mt-2 { margin-top: 1rem; }
            `}</style>
        </div>
    );
}
